/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import * as React from 'react';
import { PriceLine} from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { format } from '@msdyn365-commerce-modules/utilities';


// import currencyFormatter from "currency-formatter";

/**
 * Interface for price component resources.
 * @param {string} priceRangeSeparator - The price range separator.
 */
export interface IPriceComponentResources {
    priceRangeSeparator?: string;
}

/**
 * Interface for price component props.
 * @param {boolean} isPriceMinMaxEnabled - Whether the price range feature is enabled or not.
 * @param {IPriceComponentResources} priceResources - The price range resources.
 */
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    isPriceMinMaxEnabled?: boolean;
    isSalesAgreementPriceFeatureEnabled?: boolean;
    salesAgreementLineId?: number;
    priceResources?: IPriceComponentResources;
    currencyCode?:string;
}
export interface IPriceComponent extends IComponent<IPriceComponentProps> {
}

const PriceComponentActions = {};

const Price: React.FC<any> = (props: any) => {
    // CustomerContextualPrice could be 0
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

        // Determine if contract price is avaiable/applied by:
    //   isFeatureEnabled and SA price != 0, for PDP.
    //   SA line id != 0, for cart line.
    if ((props.isSalesAgreementPriceFeatureEnabled && props.data.price.SalesAgreementPrice !== 0) ||
        (ObjectExtensions.isNumber(props.salesAgreementLineId) && props.salesAgreementLineId !== 0)) {
        return (
            <span className={classnames('msc-price', props.className)}>
                {renderSalesAgreementPrice(props)}
            </span>
        );
    }

    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: any) => {
    const originalPrice = getOriginalPrice(props);
   

    if (originalPrice && props.data.price.CustomerContextualPrice) {
       
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

const getOriginalPrice = (props: any) => {
    return Math.max(
      props.data.price.CustomerContextualPrice + props.data.price.DiscountAmount
    );
};

const formatCurrency = (props: any, price: number | undefined, shouldUseFreePriceText: boolean | undefined): string | undefined => {
    if (price === undefined) {
        return undefined;
    }

    if (shouldUseFreePriceText && price === 0 && props.freePriceText) {
        return props.freePriceText;
    }

    // return props.context.cultureFormatter.formatCurrency(price);
    return props.context.cultureFormatter.formatCurrency(Number(price));
};

const renderCurrentPrice = (props: any): JSX.Element | undefined => {
    const initialPrice = formatCurrency(props, props.data.price.CustomerContextualPrice, true);
    const maxVariantPrice = formatCurrency(props, props.data.price.MaxVariantPrice, false);
    const minVariantPrice = formatCurrency(props, props.data.price.MinVariantPrice, false);
    if (props.isPriceMinMaxEnabled && maxVariantPrice !== minVariantPrice && maxVariantPrice && minVariantPrice) {
        return (
            <span className='msc-price__pricerange' itemProp='price'>
                <span className='msc-price__minprice' itemProp='price'>
                    {minVariantPrice}
                </span>
                <span className='msc-price__separator' itemProp='price'>
                    {props.priceResources?.priceRangeSeparator}
                </span>
                <span className='msc-price__maxprice' itemProp='price'>
                    {maxVariantPrice}
                </span>
            </span>
        );
    }
    return (<span className='msc-price__actual' itemProp='price'>
        {initialPrice}
    </span>);
};

const renderCurrentPriceWithOriginalPrice = (props: any): JSX.Element | null => {
    const originalPrice = getOriginalPrice(props);
    const initialPrice = formatCurrency(props, originalPrice, true);

    return (
        <>
            <span className='sr-only'>
                {' '}
                {props.originalPriceText}
                {' '}
                {initialPrice}
                {' '}
                {props.currentPriceText}
                {' '}
                {renderCurrentPrice(props)}
            </span>
            <span className='msc-price__strikethrough' aria-hidden='true'>
                {initialPrice}
            </span>
            <span aria-hidden='true'>
                {renderCurrentPrice(props)}
            </span>
            {props.savingsText && <span className='msc-price__savings'>
                {props.savingsText}
            </span>}
        </>
    );
};

/**
 * Return a JSX.Element contains sales agreement prompt if feature enabled.
 * @param props - The IPriceComponentProps.
 * @returns The JSX.Element.
 */
 const renderSalesAgreementPrice = (props: any): JSX.Element => {
    const priceLines: PriceLine[] = props.data.price.AttainablePriceLines ?? [];
    const firstPriceLine = priceLines[0] as any;
    return (<span className={classnames('msc-price', props.className)}>
        <p className='msc-price__actual' itemProp='price'>
            {formatCurrency(props, props.data.price.SalesAgreementPrice ?? props.data.price.CustomerContextualPrice, true)}
        </p>
        <p className={classnames('msc-price__salesAgreementPrompt', props.className)}>
            {props.priceResources?.salesAgreementPricePrompt}
        </p>
        <p className={classnames('msc-price__salesAgreementDetail', props.className)}>
            <span>
                {/* Available date */}
                {format(
                    props.priceResources?.salesAgreementExpirationDatePrompt ?? '',
                    [
                        firstPriceLine.EffectiveDate,
                        firstPriceLine.EffectiveDate
                    ]
                )}
            </span>
            <span>
                {/* Quantity commitment */}
                {format(
                    props.priceResources?.salesAgreementCommittedQuantityPrompt ?? '',
                    [firstPriceLine.CommittedQuantity]
                )}
            </span>
            <span>
                {/* Remaining quantity to qualify */}
                {format(
                    props.priceResources?.salesAgreementRemainingQuantityPrompt ?? '',
                    [firstPriceLine.RemainingQuantity]
                )}
            </span>
        </p>
    </span>);
};



export const PriceComponent: React.FunctionComponent<any> = msdyn365Commerce.createComponentOverride<any>(
    'Price',
    { component: Price, ...PriceComponentActions }
);


export default PriceComponent;